import React from "react";
import { useNavigate } from "react-router-dom";

/**
 * Renders a horizontally scrolling list of partner logos.
 * The component fetches partner data directly from `/public/data/partners.json`.
 * Each logo is a clickable link that navigates to the respective partner's page.
 * It does not accept any props.
 * @returns {JSX.Element} The rendered scrolling partner list.
 */
function PartnersListScroll()
{
    const navigate = useNavigate();
    const [partners, setPartners] = React.useState([]);

    var scrollContainerRef = React.useRef(null);
    var animationFrameId = null;
    var scrollSpeed = 0.5;
    var duplicateCount = 10;

    // This effect handles the infinite scrolling animation
    React.useEffect(function() {
        fetch('/data/partners.json')
            .then(response => response.json())
            .then(data => setPartners(data))
            .catch(error => console.error("Failed to fetch partners for scroll list:", error));
    }, []);

    React.useEffect(function()
    {
        var container = scrollContainerRef.current;
        if(!container)
        {
            return;
        }

        function step()
        {
            container.scrollLeft += scrollSpeed;

            var totalWidth = container.scrollWidth;
            var visibleWidth = container.clientWidth;

            if(container.scrollLeft >= totalWidth / 2)
            {
                container.scrollLeft = 0;
            }

            animationFrameId = window.requestAnimationFrame(step);
        }

        animationFrameId = window.requestAnimationFrame(step);

        function handleMouseEnter()
        {
            window.cancelAnimationFrame(animationFrameId);
        }

        function handleMouseLeave()
        {
            // Add a small delay before resuming animation to allow click events to process
            setTimeout(() => {
                animationFrameId = window.requestAnimationFrame(step);
            }, 100); // 100ms delay
        }

        container.addEventListener("mouseenter", handleMouseEnter);
        container.addEventListener("mouseleave", handleMouseLeave);
        container.addEventListener("touchstart", handleMouseEnter);
        container.addEventListener("touchend", handleMouseLeave);

        return function()
        {
            window.cancelAnimationFrame(animationFrameId);
            container.removeEventListener("mouseenter", handleMouseEnter);
            container.removeEventListener("mouseleave", handleMouseLeave);
            container.removeEventListener("touchstart", handleMouseEnter);
            container.removeEventListener("touchend", handleMouseLeave);
        };
    }, [partners]); // Rerun if partners data changes

    function handleLogoClick(partnerId)
    {
        // The 'mekgraj' partner has a dedicated page, all others use the dynamic route.
        if (partnerId === 'mekgraj') {
            navigate('/partners/mekgraj');
        } else {
            navigate(`/partner/${partnerId}`);
        }
    }

    function renderRepeatedImages()
    {
        var repeatedElements = [];
        if(!partners || partners.length === 0)
        {
            return repeatedElements;
        }

        for(var repeatIndex = 0; repeatIndex < duplicateCount; repeatIndex++)
        {
            for(var imageIndex = 0; imageIndex < partners.length; imageIndex++)
            {
                const partner = partners[imageIndex];
                repeatedElements.push(
                    <div 
                        key={"partner-" + repeatIndex + "-" + imageIndex}
                        className="uk-flex uk-flex-middle uk-margin-right"
                        style={{ flex: "0 0 auto" }}
                        onClick={() => handleLogoClick(partner.id)}
                    >
                        <img 
                            src={partner.logoUrl} 
                            alt={partner.name} 
                            style={{
                                height: "15vh",
                                marginRight: "60px",
                                cursor: "pointer",
                                userSelect: "none"
                            }}
                        />
                    </div>
                );
            }
        }

        return repeatedElements;
    }

    return(
        <div 
            className="uk-overflow-auto uk-flex uk-flex-nowrap uk-padding-small uk-background-muted"
            ref={scrollContainerRef}
            style={{
                whiteSpace: "nowrap",
                overflowX: "auto",
                scrollbarWidth: "none"
            }}
        >
            {renderRepeatedImages()}
        </div>
    );
}

export default PartnersListScroll;
